/** Superadmin → Settings → Agents (UI PREVIEW).
 *
 *  Clickable prototype only — every agent, message and reply below is mock
 *  data held in component state. Nothing is sent to the backend. Gated behind
 *  the AGENTS_UI_PREVIEW build flag (see lib/featureFlags.js). */
import { useEffect, useMemo, useRef, useState } from "react";
import { Send, Trash2, Server, Sparkles, Gem, Zap, ShieldCheck, ShieldOff, FlaskConical } from "lucide-react";
import Avatar from "@/components/dashboard/chat/Avatar";

const minsAgo = (m) => new Date(Date.now() - m * 60000).toISOString();

const AGENTS = [
  { id: "server", name: "Server", description: "Connected to production VPS", icon: Server, status: "online", permissions: "Read / Write", lastActivity: minsAgo(2) },
  { id: "claude", name: "Claude", icon: Sparkles, status: "disconnected", permissions: "None", lastActivity: null },
  { id: "gemini", name: "Gemini", icon: Gem, status: "disconnected", permissions: "None", lastActivity: null },
  { id: "grok", name: "Grok", icon: Zap, status: "disconnected", permissions: "None", lastActivity: null },
];

const SEED = {
  server: [
    { id: "s1", from: "agent", body: "Server agent online. Host elite-prod-01 · uptime 14d 6h · load 0.42.", at: minsAgo(38) },
    { id: "s2", from: "me", body: "Status of the nightly backup?", at: minsAgo(36) },
    { id: "s3", from: "agent", body: "Last backup finished 02:14 — 1.8 GB, 41 collections, checksum OK. Next run tonight at 02:00.", at: minsAgo(36) },
    { id: "s4", from: "me", body: "Any containers restarting?", at: minsAgo(4) },
    { id: "s5", from: "agent", body: "All 6 containers healthy. No restarts in the last 24h. Disk 61% used on /var/lib/docker.", at: minsAgo(2) },
  ],
};

const MOCK_REPLIES = [
  "Acknowledged. (Preview mode — this reply is mock data; no command was run.)",
  "CPU 7%, memory 3.1 / 8 GB, 0 failed systemd units. (mock)",
  "Queued for review. Destructive actions need superadmin confirmation. (mock)",
];

function timeAgo(iso) {
  if (!iso) return "Never";
  const m = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
}
const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

function StatusDot({ online }) {
  return <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${online ? "bg-emerald-500" : "bg-white/25"}`} />;
}

export default function AgentsPanel() {
  const [agents, setAgents] = useState(AGENTS);
  const [activeId, setActiveId] = useState("server");
  const [threads, setThreads] = useState(SEED);
  const [draft, setDraft] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);
  const replyIdx = useRef(0);
  const scrollRef = useRef(null);

  const agent = agents.find((a) => a.id === activeId);
  const online = agent.status === "online";
  const messages = useMemo(() => threads[activeId] || [], [threads, activeId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, activeId]);

  useEffect(() => { setConfirmClear(false); setDraft(""); }, [activeId]);

  const touch = (id) => setAgents((list) => list.map((a) => (a.id === id ? { ...a, lastActivity: new Date().toISOString() } : a)));

  const send = () => {
    const body = draft.trim();
    if (!body || !online) return;
    const id = activeId;
    const now = new Date().toISOString();
    setThreads((t) => ({ ...t, [id]: [...(t[id] || []), { id: `m${Date.now()}`, from: "me", body, at: now }] }));
    setDraft("");
    touch(id);
    const reply = MOCK_REPLIES[replyIdx.current++ % MOCK_REPLIES.length];
    setTimeout(() => {
      setThreads((t) => ({ ...t, [id]: [...(t[id] || []), { id: `r${Date.now()}`, from: "agent", body: reply, at: new Date().toISOString() }] }));
      touch(id);
    }, 600);
  };

  const clear = () => {
    setThreads((t) => ({ ...t, [activeId]: [] }));
    setConfirmClear(false);
  };

  return (
    <div className="space-y-3" data-testid="agents-panel">
      <div className="flex items-center gap-2 rounded-md border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-200" data-testid="agents-preview-banner">
        <FlaskConical className="h-3.5 w-3.5 shrink-0" />
        UI preview — mock data only. Messages are not sent anywhere.
      </div>

      <div className="flex h-[520px] flex-col overflow-hidden rounded-lg border border-white/10 sm:flex-row">
        {/* left: agent list */}
        <aside className="shrink-0 border-b border-white/10 bg-white/[0.03] sm:w-56 sm:border-b-0 sm:border-r" data-testid="agents-list">
          <p className="px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">Agents</p>
          <ul className="flex gap-1 overflow-x-auto p-2 sm:flex-col sm:overflow-visible">
            {agents.map((a) => {
              const isOn = a.status === "online";
              const Icon = a.icon;
              const selected = a.id === activeId;
              return (
                <li key={a.id} className="shrink-0">
                  <button
                    type="button"
                    onClick={() => setActiveId(a.id)}
                    data-testid={`agent-item-${a.id}`}
                    aria-current={selected ? "true" : undefined}
                    className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors ${
                      selected ? "bg-[#1a3fd1] text-white" : "text-white/80 hover:bg-white/5"
                    }`}
                  >
                    <Icon className={`h-4 w-4 shrink-0 ${selected ? "text-white" : "text-white/50"}`} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{a.name}</span>
                      {a.description && (
                        <span className={`block truncate text-[11px] ${selected ? "text-white/75" : "text-white/45"}`} data-testid={`agent-desc-${a.id}`}>
                          {a.description}
                        </span>
                      )}
                      <span className={`flex items-center gap-1.5 text-[11px] ${selected ? "text-white/80" : "text-white/45"}`}>
                        <StatusDot online={isOn} />
                        <span data-testid={`agent-status-${a.id}`}>{isOn ? "Online" : "Not Connected"}</span>
                      </span>
                    </span>
                    <span className={`hidden text-[10px] sm:block ${selected ? "text-white/70" : "text-white/35"}`} data-testid={`agent-last-${a.id}`}>
                      {timeAgo(a.lastActivity)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* right: conversation */}
        <section className="flex min-h-0 min-w-0 flex-1 flex-col" data-testid="agents-chat">
          <header className="flex flex-wrap items-center gap-2 border-b border-white/10 px-4 py-3">
            <Avatar name={agent.name} size={32} online={online} testId="agent-header-avatar" />
            <div className="mr-1 min-w-0">
              <h3 className="text-base font-semibold leading-tight text-white" data-testid="agent-header-name">{agent.name}</h3>
              {agent.description && <p className="truncate text-[11px] text-white/45" data-testid="agent-header-desc">{agent.description}</p>}
            </div>
            <span
              data-testid="agent-header-status"
              className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ${
                online ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-white/50"
              }`}
            >
              {online ? "Online" : "Offline"}
            </span>
            <span
              data-testid="agent-header-permissions"
              className={`flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ${
                agent.permissions === "None" ? "bg-white/10 text-white/50" : "bg-[#1a3fd1] text-white"
              }`}
            >
              {agent.permissions === "None" ? <ShieldOff className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
              {agent.permissions}
            </span>
            <div className="ml-auto flex items-center gap-1">
              {confirmClear ? (
                <>
                  <span className="text-xs text-white/50">Clear conversation?</span>
                  <button onClick={clear} data-testid="agent-clear-confirm"
                    className="rounded-full bg-red-500 px-2.5 py-0.5 text-[11px] font-semibold text-white hover:bg-red-600">Clear</button>
                  <button onClick={() => setConfirmClear(false)} data-testid="agent-clear-cancel"
                    className="rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-semibold text-white/70 hover:bg-white/15">Cancel</button>
                </>
              ) : (
                <button
                  onClick={() => setConfirmClear(true)}
                  disabled={messages.length === 0}
                  data-testid="agent-clear-btn"
                  className="flex items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1 text-xs text-white/70 transition-colors hover:border-red-400/60 hover:text-red-300 disabled:pointer-events-none disabled:opacity-30"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Clear conversation
                </button>
              )}
            </div>
          </header>

          {/* history — bubble styling mirrors chat/ChatThread.js */}
          <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto bg-black/20 px-4 py-3" data-testid="agent-history">
            {messages.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center text-sm text-white/40" data-testid="agent-history-empty">
                {online ? (
                  <p>No messages yet. Send a prompt to {agent.name}.</p>
                ) : (
                  <>
                    <p className="font-medium text-white/60">{agent.name} is not connected</p>
                    <p className="mt-1 max-w-xs text-xs">Connecting agents isn't part of this preview. Once connected, the conversation will appear here.</p>
                  </>
                )}
              </div>
            ) : (
              messages.map((m) => {
                const mine = m.from === "me";
                return (
                  <div key={m.id} className={`mt-2 flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`agent-msg-${m.id}`}>
                    {!mine && (
                      <div className="mr-1.5 w-6 shrink-0 self-end pb-4">
                        <Avatar name={agent.name} size={24} showStatus={false} />
                      </div>
                    )}
                    <div className={`flex max-w-[75%] flex-col ${mine ? "items-end" : "items-start"}`}>
                      <div className={`rounded-2xl px-3 py-1.5 text-sm leading-snug ${mine ? "bg-[#1a3fd1] text-white" : "bg-white/10 text-white/90"}`}>
                        <p className="whitespace-pre-wrap break-words">{m.body}</p>
                      </div>
                      <p className={`mt-0.5 text-[9px] text-white/35 ${mine ? "mr-1" : "ml-1"}`}>{fmtTime(m.at)}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* composer */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex shrink-0 items-end gap-2 border-t border-white/10 px-3 py-2"
          >
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
              title="Enter to send · Shift+Enter for a new line"
              disabled={!online}
              placeholder={online ? `Prompt ${agent.name}…` : `${agent.name} is not connected`}
              data-testid="agent-prompt-input"
              className="max-h-32 min-h-[38px] min-w-0 flex-1 resize-none rounded-2xl border border-white/15 bg-white/5 px-3.5 py-2 text-sm text-white placeholder:text-white/40 focus:border-[#1a3fd1] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={!online || !draft.trim()}
              data-testid="agent-send-btn"
              className="flex h-[38px] items-center gap-1.5 rounded-full bg-[#1a3fd1] px-4 text-sm font-medium text-white transition-colors hover:bg-[#1633b8] disabled:opacity-30"
            >
              <Send className="h-4 w-4" /> Send
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
